import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StructureImage from './StructureImage';
import ComplexStructureWindow from './ComplexStructureWindow';
import { getStructuralEvidence, STRUCTURES } from '../../data/ppiStructuralEvidence';

interface Props {
  molecule: { id: number; smiles: string; inchi_key?: string | null; properties: Record<string, any> };
  onClose: () => void;
  onShowResults: () => void;
}

export default function PpiComplexDialog({ molecule, onClose, onShowResults }: Props) {
  const evidence = getStructuralEvidence(molecule);
  const [selected, setSelected] = useState(0);
  const [showBoundPose, setShowBoundPose] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const name = molecule.properties.name || molecule.properties.Name || `Molecule #${molecule.id}`;
  const reference = evidence ? STRUCTURES[evidence.complexes[selected] || evidence.complexes[0]] : null;

  useEffect(() => { setSelected(0); }, [molecule.id]);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); onCloseRef.current(); }
      if (event.key === 'Tab') {
        const items = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], select, [tabindex="0"]');
        if (!items?.length) return;
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', keydown, true);
    return () => {
      window.removeEventListener('keydown', keydown, true);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(<div className="fixed inset-0 bg-slate-950/60 flex items-center justify-center p-3" style={{ zIndex: 1200 }}>
    <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="ppi-complex-title" className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[94vh] overflow-auto">
      <header className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between gap-4">
        <div><h2 id="ppi-complex-title" className="font-semibold text-slate-900">Interaction & binding structures · {String(name)}</h2>
          <p className="text-xs text-slate-500 mt-1">Public structural evidence · linked to the compound shown below and its experimental results</p></div>
        <button ref={close} onClick={onClose} className="text-sm text-teal-700 rounded border border-teal-200 px-3 py-2 shrink-0">Back to workbench</button>
      </header>
      <div className="p-4 space-y-4">
        {evidence && reference ? <>
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-950 text-sm p-3">{evidence.interpretation}</div>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="ppi-partner" className="text-sm font-medium text-slate-700">Protein interaction reference</label>
            <select id="ppi-partner" value={selected} onChange={event => setSelected(Number(event.target.value))} className="rounded border px-3 py-2 bg-white text-sm">
              {evidence.complexes.map((id, index) => <option key={id} value={index}>{STRUCTURES[id].target} + {STRUCTURES[id].partner} ({id})</option>)}
            </select>
          </div>
          <div className="space-y-4">
            <ComplexStructureWindow key={reference.pdbId} reference={reference} allowComponentViews />
            {evidence.boundPose ? <>
              <button onClick={() => setShowBoundPose(value => !value)} aria-expanded={showBoundPose} className="text-teal-700 font-medium text-sm underline">{showBoundPose ? 'Hide' : 'Compare'} experimental compound-bound structure ({evidence.boundPose})</button>
              {showBoundPose && <ComplexStructureWindow key={evidence.boundPose} reference={STRUCTURES[evidence.boundPose]} />}
            </> :
              <section className="rounded-xl border border-dashed border-slate-300 p-8 flex flex-col justify-center text-slate-600 bg-white">
                <h3 className="font-semibold mb-2">No verified compound-bound pose linked</h3>
                <p className="text-sm">This curated set does not contain a verified co-crystal for {evidence.name}. A different compound or predicted pose has not been substituted. This does not mean no structure exists.</p>
              </section>}
          </div>
        </> : <section className="bg-white border rounded-xl p-6">
          <h3 className="font-semibold text-slate-800">No verified complex linked to this chemical identity</h3>
          <p className="text-sm text-slate-600 mt-2">Structures are matched by exact InChIKey, not by compound name or file row. A PDB reference, the correct partner chains and the evidence type need to be verified before showing a complex here.</p>
          <p className="text-sm text-slate-600 mt-2">No predicted complex or binding pose has been generated.</p>
        </section>}
        <section className="bg-white border rounded-xl px-4 py-3 flex flex-wrap items-center gap-5">
          <StructureImage moleculeId={molecule.id} smiles={molecule.smiles || undefined} size={130} />
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-semibold text-slate-800">{String(name)} · your workbench compound</h3>
            <p className="text-xs text-slate-500 mt-1">Molecule #{molecule.id}{molecule.properties.reference_id ? ` · ${molecule.properties.reference_id}` : ''}. Coordinates are public references, not a model calculated from this record.</p>
            <p className="text-sm text-slate-600 mt-2">Binding and screening measurements remain linked to this same compound. No binding energies or disruption outcomes are inferred from these views.</p>
            <button onClick={onShowResults} className="mt-3 rounded-lg bg-teal-700 text-white text-sm px-4 py-2">View linked experimental results →</button>
          </div>
        </section>
        <p className="text-xs text-slate-500">Partner panes share the complex coordinates and camera. The optional compound-bound comparison is a separate experiment, not spatially aligned or a before/after experiment. Contact vicinity is not a hydrogen-bond, hotspot or pocket prediction. Reference selection verified 2 October 2026; no predicted structures included.</p>
      </div>
    </div>
  </div>, document.body);
}
